import { useEffect, useRef, useState, ReactNode } from 'react'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'
import style from './ItemsCarousel.module.scss'

interface ItemsCarouselProps {
  children: ReactNode[]
  itemsPerPage?: number // For desktop, how many items visible at once (default: 4)
  gap?: number // Gap between items in pixels (default: 10)
  itemWidth?: number // Fixed width for each item (desktop only)
  maxWidth?: number // Maximum width constraint for carousel (prevents parent stretching)
  className?: string
}

const ItemsCarousel = ({
  children,
  itemsPerPage = 4,
  gap = 10,
  itemWidth,
  maxWidth,
  className = ''
}: ItemsCarouselProps) => {
  const viewport = useViewport()
  const containerRef = useRef<HTMLDivElement>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [calculatedItemWidth, setCalculatedItemWidth] = useState<number | null>(null)
  const [visibleItems, setVisibleItems] = useState(itemsPerPage)

  const isMobile = viewport.mobile

  // Calculate optimal item width and visible items count
  useEffect(() => {
    if (isMobile || itemWidth) return

    const calculateOptimalLayout = () => {
      if (!containerRef.current) return

      // Get actual available width for items
      let containerWidth = containerRef.current.clientWidth
      // Apply maxWidth constraint if provided
      if (maxWidth && containerWidth > maxWidth) {
        containerWidth = maxWidth
      }
      const MIN_ITEM_WIDTH = 160
      const MAX_ITEM_WIDTH = 200

      // Try to fit as many items as possible within the width constraints
      let optimalItemsCount = Math.floor(containerWidth / (MIN_ITEM_WIDTH + gap))

      // Ensure at least 1 item and don't show more items than we have
      optimalItemsCount = Math.max(1, Math.min(optimalItemsCount, children.length))

      // Calculate item width to fill the container (accounting for gap after each item)
      let calculatedWidth = Math.min((containerWidth - gap * optimalItemsCount) / optimalItemsCount, MAX_ITEM_WIDTH)

      // If calculated width is too large, reduce items count
      while (calculatedWidth > MAX_ITEM_WIDTH && optimalItemsCount < children.length) {
        optimalItemsCount++
        calculatedWidth = (containerWidth - gap * optimalItemsCount) / optimalItemsCount
      }

      setVisibleItems(optimalItemsCount)
      setCalculatedItemWidth(calculatedWidth)
    }

    // Initial calculation with a small delay to ensure container is rendered
    const timer = setTimeout(calculateOptimalLayout, 0)

    window.addEventListener('resize', calculateOptimalLayout)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', calculateOptimalLayout)
    }
  }, [isMobile, itemWidth, gap, children.length, maxWidth])

  const effectiveItemWidth = itemWidth || calculatedItemWidth
  const effectiveItemsPerPage = itemWidth ? itemsPerPage : visibleItems
  const allItemsFit = !isMobile && children.length <= effectiveItemsPerPage

  const updateScrollState = () => {
    if (!containerRef.current) return

    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current
    const threshold = 5 // 5px threshold for floating point errors

    setCanScrollLeft(scrollLeft > threshold)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - threshold)

    // Update current index based on scroll position
    if (!isMobile && effectiveItemWidth) {
      const singleItemWidth = effectiveItemWidth + gap
      const newIndex = Math.round(scrollLeft / singleItemWidth)
      if (newIndex !== currentPage) {
        setCurrentPage(newIndex)
      }
    }
  }

  const scrollByItems = (direction: 1 | -1) => {
    if (!containerRef.current || !effectiveItemWidth) return

    const singleItemWidth = effectiveItemWidth + gap
    const scrollAmount = singleItemWidth * direction

    containerRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    })
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    updateScrollState()

    container.addEventListener('scroll', updateScrollState, { passive: true })
    return () => container.removeEventListener('scroll', updateScrollState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, itemWidth, gap, itemsPerPage, calculatedItemWidth, visibleItems])

  // Prevent vertical scroll propagation on horizontal scroll (desktop only)
  useEffect(() => {
    const container = containerRef.current
    if (!container || isMobile) return

    const handleWheel = (event: WheelEvent) => {
      if (event.deltaX !== 0) {
        event.stopPropagation()
        event.preventDefault()
      }
    }

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => container.removeEventListener('wheel', handleWheel)
  }, [isMobile])

  // Reset to first page on viewport change
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ left: 0, behavior: 'auto' })
      setCurrentPage(0)
    }
  }, [viewport.width, isMobile])

  return (
    <div className={`${style.wrapper} ${className}`} style={maxWidth ? { maxWidth: `${maxWidth}px` } : undefined}>
      <div className={`${style.content} ${allItemsFit ? style.noArrows : ''}`}>
        {!isMobile && !allItemsFit && canScrollLeft && (
          <button
            className={`${style.arrow} ${style.left}`}
            onClick={() => scrollByItems(-1)}
            aria-label="Previous item"
            type="button"
          >
            <i className="pi pi-chevron-left" />
          </button>
        )}

        <div className={style.container} ref={containerRef}>
          <div
            className={style.track}
            style={{
              gap: `${gap}px`,
              // Mobile: each item takes 60% of viewport, allows peeking next item
              // Desktop: use calculated width for perfect fit
              gridTemplateColumns: isMobile
                ? `repeat(${children.length}, 60vw)`
                : effectiveItemWidth
                  ? `repeat(${children.length}, ${effectiveItemWidth}px)`
                  : `repeat(${children.length}, calc((100% - ${gap * (effectiveItemsPerPage - 1)}px) / ${effectiveItemsPerPage}))`
            }}
          >
            {children.map((child, index) => (
              <div key={index} className={style.item}>
                {child}
              </div>
            ))}
          </div>
        </div>

        {!isMobile && !allItemsFit && canScrollRight && (
          <button
            className={`${style.arrow} ${style.right}`}
            onClick={() => scrollByItems(1)}
            aria-label="Next item"
            type="button"
          >
            <i className="pi pi-chevron-right" />
          </button>
        )}
      </div>
    </div>
  )
}

export default ItemsCarousel
